import { Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { HttpUpdateForm } from '@/api';
import { InlineTextEditor } from '@/components/Ui/InlineTextEditor';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section } from '@/components/Ui/Section';
import { SegmentedButtons } from '@/components/Ui/SegmentedButtons';
import { usePlanRetention } from '@/hooks/usePlanRetention';
import { shortDate } from '@/lib/relative-time';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

const MAX_ORIGINS = 20;

export function AfterSubmissionSection({
  form,
  save,
  errors,
  data,
}: Pick<SectionProps, 'form' | 'save' | 'errors' | 'data'>) {
  const { t, i18n } = useTranslation();
  const retention = usePlanRetention();
  const origins = form.allowed_origins ?? [];
  const days = form.retention_days;
  const nostr = form.encryption_mode === 'nostr';
  const contentDefault = form.encryption_mode === 'server' ? 'notification' : 'full';
  const content = nostr
    ? 'notification'
    : form.email_notification_type === 'notification'
      ? 'notification'
      : 'full';
  const format = form.email_notification_format === 'text' ? 'text' : 'html';
  const originProblem = data.problems?.find((p) => p.cause === 'origin_rejections');

  const saveText = async (patch: HttpUpdateForm, message: string, close: () => void) => {
    if (!(await save(patch, message))) throw new Error('rejected');
    close();
  };

  const retentionDays =
    typeof days === 'number' && !retention.options.includes(days)
      ? [...retention.options, days].sort((a, b) => a - b)
      : retention.options;
  const retentionOptions = [...retentionDays].reverse().map((d) => ({
    value: String(d),
    label:
      d === retention.max
        ? t('create.keepPlanMax', { label: t('create.days', { count: d }) })
        : t('create.days', { count: d }),
  }));

  return (
    <Section title={t('form.after.title')}>
      <LedgerRow
        id="thanks"
        label={t('form.after.thanks')}
        value={
          form.redirect_url ? (
            <span className={classes.mono}>{form.redirect_url}</span>
          ) : (
            t('form.after.thanksNone')
          )
        }
        help={t('form.after.thanksHelp')}
        editor={{
          render: (close) => (
            <InlineTextEditor
              value={form.redirect_url ?? ''}
              mono
              label={t('form.after.thanks')}
              placeholder="https://example.com/thanks"
              help={t('form.after.thanksEditHelp')}
              error={errors.redirect_url}
              onCancel={close}
              onSave={(next) =>
                saveText(
                  { redirect_url: next.trim() },
                  next.trim() ? t('form.after.thanksSaved') : t('form.after.thanksCleared'),
                  close
                )
              }
            />
          ),
        }}
      />
      <LedgerRow
        id="origins"
        label={t('form.after.origins')}
        value={
          origins.length > 0 ? (
            <span className={classes.mono}>
              {origins.map((o) => (
                <span key={o} className={classes.line}>
                  {o}
                </span>
              ))}
            </span>
          ) : (
            t('form.after.anySite')
          )
        }
        risk={
          originProblem
            ? originProblem.detail
              ? t('form.after.originsRiskDetail', {
                  count: originProblem.count,
                  detail: originProblem.detail,
                })
              : t('form.after.originsRisk', { count: originProblem.count })
            : undefined
        }
        help={t('form.after.originsHelp')}
        changed={
          origins.length > 0
            ? {
                defaultLabel: t('form.after.anySiteLower'),
                onReset: () => void save({ allowed_origins: [] }, t('form.after.originsCleared')),
              }
            : undefined
        }
        editor={{
          render: (close) => (
            <InlineTextEditor
              value={origins.join('\n')}
              multiline
              mono
              label={t('form.after.origins')}
              placeholder="https://www.example.com"
              help={t('form.after.originsEditHelp', { max: MAX_ORIGINS })}
              error={errors.allowed_origins}
              onCancel={close}
              onSave={(next) => {
                const list = next
                  .split(/[\n,]/)
                  .map((o) => o.trim())
                  .filter(Boolean);
                return saveText(
                  { allowed_origins: list },
                  list.length > 0 ? t('form.after.originsSaved') : t('form.after.originsCleared'),
                  close
                );
              }}
            />
          ),
        }}
      />
      <LedgerRow
        label={t('form.after.keep')}
        value={
          typeof days !== 'number' ? (
            t('form.after.keepNone')
          ) : form.retention_capped_at ? (
            <>
              <strong>{t('create.days', { count: days })}</strong>
              <span className={classes.help}>
                {t('form.after.keepLowered', {
                  label: t('create.days', { count: days }),
                  date: shortDate(form.retention_capped_at, i18n.language, true),
                })}
              </span>
            </>
          ) : (
            t('create.days', { count: days })
          )
        }
        help={
          typeof days === 'number' ? t('form.after.keepHelp', { max: retention.max }) : undefined
        }
        changed={
          days === retention.max
            ? undefined
            : {
                defaultLabel: t('create.keepPlanMax', {
                  label: t('create.days', { count: retention.max }),
                }),
                onReset: () =>
                  void save(
                    { retention_days: retention.max },
                    t('form.after.keepSaved', { label: t('create.days', { count: retention.max }) })
                  ),
              }
        }
        editor={{
          render: (close) => (
            <Select
              className={classes.select}
              aria-label={t('form.after.keep')}
              data={retentionOptions}
              value={typeof days === 'number' ? String(days) : null}
              placeholder={t('form.after.keepNone')}
              allowDeselect={false}
              defaultDropdownOpened
              onDropdownClose={close}
              onChange={(v) => {
                if (!v) return;
                close();
                void save(
                  { retention_days: Number(v) },
                  t('form.after.keepSaved', { label: t('create.days', { count: Number(v) }) })
                );
              }}
            />
          ),
        }}
      />
      <LedgerRow
        label={t('form.after.formatLabel')}
        value={t(`form.after.format.${format}`)}
        help={t('form.after.formatHelp')}
        editor={{
          render: (close) => (
            <SegmentedButtons
              aria-label={t('form.after.formatLabel')}
              options={[
                { value: 'html', label: t('form.after.format.html') },
                { value: 'text', label: t('form.after.format.text') },
              ]}
              value={format}
              onChange={(v) => {
                close();
                void save(
                  { email_notification_format: v },
                  t('form.after.formatSaved', { format: t(`form.after.format.${v}`) })
                );
              }}
            />
          ),
        }}
      />
      <LedgerRow
        label={t('form.after.content')}
        value={t(`form.after.contentValue.${content}`)}
        help={nostr ? t('form.after.contentNostr') : t(`form.after.contentHelp.${content}`)}
        changed={
          nostr || content === contentDefault
            ? undefined
            : {
                defaultLabel: t(`form.after.contentValue.${contentDefault}`).toLowerCase(),
                onReset: () =>
                  void save(
                    { email_notification_type: contentDefault },
                    t('form.after.contentSaved', {
                      content: t(`form.after.contentValue.${contentDefault}`),
                    })
                  ),
              }
        }
        editor={
          nostr
            ? undefined
            : {
                render: (close) => (
                  <SegmentedButtons
                    aria-label={t('form.after.content')}
                    options={[
                      { value: 'full', label: t('form.after.contentValue.full') },
                      { value: 'notification', label: t('form.after.contentValue.notification') },
                    ]}
                    value={content}
                    onChange={(v) => {
                      close();
                      void save(
                        { email_notification_type: v },
                        t('form.after.contentSaved', {
                          content: t(`form.after.contentValue.${v}`),
                        })
                      );
                    }}
                  />
                ),
              }
        }
      />
    </Section>
  );
}
